import CircleByIdButton from '@/circle/components/CircleByIdButton'
import useDateLocale from '@/common/hooks/useDateLocale'
import { Box, Spacer, Text } from '@chakra-ui/react'
import { SearchTypes } from '@rolebase/shared/model/search'
import { capitalizeFirstLetter } from '@utils/capitalizeFirstLetter'
import { format } from 'date-fns'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchItem } from '../searchTypes'
import { searchItemTitleSeparator } from './searchResultUtils'
import SearchResultIcon from './SearchResultIcon'

interface Props {
  item?: SearchItem
  size?: string
  title: string
}

export default function SearchResultContent({ item, size, title }: Props) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()

  // ORG-01 in search results: leaf-role items are disambiguated as
  // "Name (Circle: direct parent)" (CartoGroov format) instead of the
  // collapsed breadcrumb; structural circle items keep their breadcrumb,
  // rendered in small caps.
  const isLeafRole =
    item?.type === SearchTypes.Circle &&
    item.isCircle === false &&
    !!item.parentName
  const isStructuralCircle =
    item?.type === SearchTypes.Circle && item.isCircle === true

  const displayTitle = useMemo(() => {
    if (isLeafRole && item) {
      const name =
        item.title.split(searchItemTitleSeparator).pop() ?? item.title
      return t('SearchResultContent.leafRoleTitle', {
        name,
        circle: item.parentName,
      })
    }
    return title
  }, [isLeafRole, item, title, t])

  if (!item) return null

  return (
    <>
      <SearchResultIcon item={item} size={size} />
      <Box
        ml={2}
        textAlign="left"
        sx={
          isStructuralCircle
            ? { fontVariant: 'small-caps', fontFeatureSettings: "'smcp'" }
            : undefined
        }
      >
        {displayTitle}
      </Box>

      <Spacer />

      {item.date && (
        <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.300' }}>
          {capitalizeFirstLetter(
            format(new Date(item.date), 'PPPP', {
              locale: dateLocale,
            })
          )}
        </Text>
      )}

      {item.circleId && (
        <CircleByIdButton
          id={item.circleId}
          ml={2}
          size={size === 'sm' ? 'xs' : 'sm'}
        />
      )}
    </>
  )
}
